// src/pages/LiveMap.js
import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { Spinner, formatDate } from '../components/Shared';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { defaultIcon, staleIcon } from '../utils/leafletIcons';

const MYSURU_CENTER = [12.3052, 76.6552];


export default function LiveMap() {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  async function load() {
    try {
      const data = await get('/api/deliveries/live-map');
      setPersons(data.deliveryPersons || []);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  function openGoogleMaps(lat, lng, name) {
    window.open(`https://www.google.com/maps?q=${lat},${lng}&label=${encodeURIComponent(name)}`, '_blank');
  }

  function formatElapsed(seconds) {
    if (!seconds) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Live Delivery Map</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{persons.length} active delivery person{persons.length !== 1 ? 's' : ''} — updates every 30 seconds</p>
        </div>
        <button onClick={load} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
          🔄 Refresh
        </button>
      </div>

      {persons.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-16 text-center">
          <p className="text-5xl mb-4">🗺️</p>
          <p className="text-gray-600 dark:text-gray-400 font-semibold text-lg">No active deliveries right now</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">Delivery persons will appear here when they are out on delivery</p>
        </div>
      ) : (
        <>
          {/* Overview map — every active driver's last known position at once */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" style={{ height: '400px' }}>
            {persons.some(p => p.last_lat) ? (
              <MapContainer center={MYSURU_CENTER} zoom={12} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {persons.filter(p => p.last_lat).map(p => {
                  const stale = p.seconds_since_last_ping > 300;
                  return (
                    <Marker
                      key={p.user_id}
                      position={[parseFloat(p.last_lat), parseFloat(p.last_lng)]}
                      icon={stale ? staleIcon : defaultIcon}
                      eventHandlers={{ click: () => setSelected(p) }}
                    >
                      <Popup>
                        <strong>{p.delivery_person_name}</strong><br />
                        {stale ? `Last seen ${formatElapsed(Math.round(p.seconds_since_last_ping))} ago` : 'Live'}<br />
                        {p.invoices_in_session || 0} invoice{p.invoices_in_session !== 1 ? 's' : ''} in session
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
                No active drivers have a GPS fix yet
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Person list */}
          <div className="space-y-3">
            {persons.map(p => {
              const stale = p.seconds_since_last_ping > 300;
              return (
                <div
                  key={p.user_id}
                  onClick={() => setSelected(p)}
                  className={`bg-white dark:bg-gray-800 rounded-xl border-2 p-4 cursor-pointer transition-all
                    ${selected?.user_id === p.user_id ? 'border-brand-500 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-brand-500/40'}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-gray-100">{p.delivery_person_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{p.phone}</p>
                    </div>
                    {/* Live/stale GPS signal indicator — operational status,
                        left untouched same as invoice stage colors */}
                    <div className={`w-3 h-3 rounded-full mt-1 ${stale ? 'bg-amber-400' : 'bg-green-500'}`}
                         title={stale ? 'Signal delayed' : 'Live'} />
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-gray-400 dark:text-gray-500">Session time</p>
                      <p className="font-medium text-gray-700 dark:text-gray-300">{formatElapsed(p.session_seconds)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 dark:text-gray-500">Last ping</p>
                      <p className={`font-medium ${stale ? 'text-amber-600' : 'text-green-600'}`}>
                        {p.seconds_since_last_ping ? `${formatElapsed(Math.round(p.seconds_since_last_ping))} ago` : 'No ping'}
                      </p>
                    </div>
                    {p.battery_pct && (
                      <div>
                        <p className="text-gray-400 dark:text-gray-500">Battery</p>
                        <p className={`font-medium ${p.battery_pct < 20 ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>
                          🔋 {p.battery_pct}%
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-400 dark:text-gray-500">Invoices</p>
                      <p className="font-medium text-gray-700 dark:text-gray-300">{p.invoices_in_session || 0}</p>
                    </div>
                  </div>
                  {p.last_lat && (
                    <button
                      onClick={e => { e.stopPropagation(); openGoogleMaps(p.last_lat, p.last_lng, p.delivery_person_name); }}
                      className="mt-3 w-full text-xs bg-brand-700/5 text-brand-700 py-1.5 rounded-lg hover:bg-brand-700/10 font-medium"
                    >
                      📍 Open in Google Maps
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-2">
            {selected ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="font-bold text-gray-800 dark:text-gray-100 text-lg mb-4">{selected.delivery_person_name}</h2>
                <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                  <InfoRow label="Phone" value={selected.phone} />
                  <InfoRow label="Session started" value={formatDate(selected.started_at)} />
                  <InfoRow label="Last location" value={selected.last_lat ? `${parseFloat(selected.last_lat).toFixed(5)}, ${parseFloat(selected.last_lng).toFixed(5)}` : 'No data'} />
                  <InfoRow label="GPS accuracy" value={selected.last_accuracy ? `±${selected.last_accuracy}m` : '—'} />
                </div>

                {selected.last_lat ? (
                  <div>
                    <div className="rounded-xl overflow-hidden mb-4 border border-gray-200 dark:border-gray-700" style={{ height: '256px' }}>
                      <MapContainer
                        center={[parseFloat(selected.last_lat), parseFloat(selected.last_lng)]}
                        zoom={15}
                        style={{ height: '100%', width: '100%' }}
                        key={selected.user_id}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[parseFloat(selected.last_lat), parseFloat(selected.last_lng)]} icon={defaultIcon}>
                          <Popup>{selected.delivery_person_name}</Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                    <button
                      onClick={() => openGoogleMaps(selected.last_lat, selected.last_lng, selected.delivery_person_name)}
                      className="w-full py-3 bg-brand-700 text-white rounded-xl font-semibold hover:bg-brand-500 transition-colors"
                    >
                      🗺️ Open Full Map in Google Maps
                    </button>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-xl h-48 flex items-center justify-center">
                    <p className="text-gray-400 dark:text-gray-500">No GPS data available yet</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 h-full flex items-center justify-center p-12">
                <div className="text-center">
                  <p className="text-4xl mb-3">👆</p>
                  <p className="text-gray-500 dark:text-gray-400">Select a delivery person to see their location details</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{label}</p>
      <p className="text-gray-700 dark:text-gray-300 font-medium">{value || '—'}</p>
    </div>
  );
}
